/**
 * Reading progress and sync metadata CRUD operations.
 */

import { generateId } from '../utils';
import { ensureDB, getDB, getAll, getById, put, addPendingChange, now } from './helpers';

// =====================================================
// Progress
// =====================================================

export async function getProgress(bookId, userId) {
    if (!(await ensureDB())) return null;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('progress', 'readonly');
        const store = tx.objectStore('progress');
        const index = store.index('user_book');
        const request = index.get([userId, bookId]);

        request.onsuccess = () => {
            const progress = request.result || null;
            resolve(progress);
        };
        request.onerror = () => reject(request.error);
    });
}

export async function getAllProgress(userId) {
    return getAll('progress', userId);
}

export async function updateProgress(userId, bookId, data) {
    let progress = await getProgress(bookId, userId);

    if (progress) {
        progress = {
            ...progress,
            ...data,
            // Ensure scrollTop and scrollHeight are preserved if provided
            scrollTop: data.scrollTop !== undefined ? data.scrollTop : progress.scrollTop,
            scrollHeight: data.scrollHeight !== undefined ? data.scrollHeight : progress.scrollHeight,
            // finished_at is FIRST-WRITE-WINS — MUST sit AFTER ...data or the
            // spread re-clobbers it. A re-mark must not move the original finish
            // time; a stale null must not clear it. This is the ONLY guard for
            // free/offline users (no backend round-trip). No unfinish path exists;
            // a future "mark unread" must use a distinct field, not finished_at:null.
            finished_at: progress.finished_at || data.finished_at || null,
            updated_at: now(),
            _pending: true,
        };
    } else {
        progress = {
            id: `${userId}:${bookId}`,
            user_id: userId,
            book_id: bookId,
            cfi: data.cfi || null,
            percentage: data.percentage ?? 0,
            chapter: data.chapter || null,
            page: data.page ?? null,
            scrollTop: data.scrollTop ?? null,
            scrollHeight: data.scrollHeight ?? null,
            finished_at: data.finished_at ?? null,
            updated_at: now(),
            _pending: true,
        };
    }

    await put('progress', progress);
    await addPendingChange('progress', 'upsert', progress);

    // Update book's last_read_at so "Continue Reading" order syncs across devices
    const book = await getById('books', bookId);
    if (book) {
        const updatedBook = { ...book, last_read_at: now(), updated_at: now(), _pending: true };
        await put('books', updatedBook);
        await addPendingChange('books', 'update', updatedBook);
    }

    return progress;
}

// =====================================================
// Sync metadata
// =====================================================

export async function getLastSyncTime() {
    if (!(await ensureDB())) return null;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('sync_meta', 'readonly');
        const store = tx.objectStore('sync_meta');
        const request = store.get('last_sync');

        request.onsuccess = () => resolve(request.result?.value || null);
        request.onerror = () => reject(request.error);
    });
}

export async function setLastSyncTime(time) {
    if (!(await ensureDB())) return;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('sync_meta', 'readwrite');
        const store = tx.objectStore('sync_meta');
        const request = store.put({ key: 'last_sync', value: time });

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}
